import { inject, Injector, Service } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import type {
  OnyxCoordinates,
  OnyxCustomAddress,
  OnyxDropdownOptions,
} from '@onyx/angular';
import {
  ActionHelper,
  AddressHelper,
  DropdownHelper,
  OnyxFilterPipe,
  OnyxMapVehicleStatus,
  OnyxNamePipe,
  OnyxScheduleCompliance,
  OnyxToastService,
} from '@onyx/angular';
import { chain, compact, min, minBy, orderBy, uniq } from 'lodash';
import { DateTime, Duration } from 'luxon';
import { DelegationStatus } from '../../dashboard/delegations/common/enums/delegation-status';
import { DelegationsRoute } from '../../dashboard/delegations/common/enums/delegations-route';
import { DelegationHelper } from '../../dashboard/delegations/common/helpers/delegation.helper';
import { DelegationsService } from '../../dashboard/delegations/common/services/delegations.service';
import { DelegationActionModalMode } from '../../dashboard/delegations/delegation-action-modal/delegation-action-modal.component';
import { GanttQueryParamKey } from '../../dashboard/gantt/common/enums/gantt-query-param-key';
import { GanttRoute } from '../../dashboard/gantt/common/enums/gantt-route';
import { MapQueryParamKey } from '../../dashboard/map/common/enums/map-query-param-key';
import { MapRoute } from '../../dashboard/map/common/enums/map-route';
import { TelematicsHelper } from '../../dashboard/map/common/helpers/telematics.helper';
import { MapStore } from '../../dashboard/map/common/stores/map.store';
import { OrderPointCategory } from '../../dashboard/orders/common/enums/order-point-category';
import { OrderPointStatus } from '../../dashboard/orders/common/enums/order-point-status';
import { OrderStatus } from '../../dashboard/orders/common/enums/order-status';
import { FuelType } from '../enums/fuel-type';
import { TelematicsCargoFilter } from '../enums/telematics/telematics-cargo-filter';
import { TelematicsCrewFilter } from '../enums/telematics/telematics-crew-filter';
import { TelematicsDelegationFilter } from '../enums/telematics/telematics-delegation-filter';
import { TelematicsRemainingFuelFilter } from '../enums/telematics/telematics-remaining-fuel-filter';
import { TelematicsRouteFilter } from '../enums/telematics/telematics-route-filter';
import { TelematicsSort } from '../enums/telematics/telematics-sort';
import { TelematicsStatusFilter } from '../enums/telematics/telematics-status-filter';
import { TelematicsTrailerFilter } from '../enums/telematics/telematics-trailer-filter';
import { CommonHelper } from '../helpers/common.helper';
import { RouteHelper } from '../helpers/route.helper';
import type { TelematicsFilters } from '../interfaces/telematics/telematics-filters';
import type { TelematicsItem } from '../interfaces/telematics/telematics-item';
import { TelematicsIdentifierPipe } from '../pipes/telematics-fleet-identifier.pipe';
import { FleetHelper } from './../../dashboard/fleet/common/helpers/fleet.helper';
import { FleetTypeHelper } from './../../dashboard/fleet/common/helpers/fleet-type.helper';

@Service()
export class TelematicsService {
  private readonly filterPipe = inject(OnyxFilterPipe);
  private readonly telematicsIdentifierPipe = inject(TelematicsIdentifierPipe);
  private readonly toastService = inject(OnyxToastService);
  private readonly router = inject(Router);
  private readonly fleetHelper = inject(FleetHelper);
  private readonly namePipe = inject(OnyxNamePipe);
  private readonly delegationsService = inject(DelegationsService);
  private readonly delegationHelper = inject(DelegationHelper);
  private readonly injector = inject(Injector);
  private readonly translateService = inject(TranslateService);

  public getOptions(
    item: TelematicsItem,
    context: 'gantt' | 'map',
    injector: Injector,
  ): OnyxDropdownOptions<() => void> {
    const delegations = chain(item.drivers)
      .map((driver) => ({
        driver,
        delegation: driver.delegation,
      }))
      .groupBy((driver) => driver.delegation?.uuid)
      .values()
      .map((drivers) => ({
        delegation: drivers[0].delegation,
        drivers: drivers.map(({ driver }) => driver),
      }))
      .value();

    return [
      {
        id: '0.default',
        options: [
          {
            name: `${this.translateService.instant('map.routeCalculator.valuateOrder')}...`,
            value: () =>
              CommonHelper.showOnMap(this.router, item.uuid, this.injector, {
                showRouteCalculator: true,
              }),
            leftIcon: { name: 'calculate-outlined', size: 20 },
            disabled: !item.telemetry,
            tooltip: !item.telemetry ? 'labels.noTelematicsData' : undefined,
          } as const,
          {
            name: 'labels.scheduleService',
            value: () =>
              CommonHelper.showOnMap(this.router, item.uuid, this.injector, {
                showService: true,
              }),
            leftIcon: { name: 'service-outlined', size: 20 },
          } as const,
        ],
      },
      {
        id: '1.actions',
        options: [
          {
            name: 'labels.share',
            value: () => {
              const route = this.router
                .createUrlTree(
                  [context === 'gantt' ? GanttRoute.GANTT : MapRoute.MAP],
                  {
                    queryParams: {
                      [context === 'gantt'
                        ? GanttQueryParamKey.VEHICLE_UUID
                        : MapQueryParamKey.VEHICLE_UUID]: item.uuid,
                    },
                  },
                )
                .toString();
              ActionHelper.share(route, this.toastService);
            },
            leftIcon: { name: 'share-outlined', size: 20 },
          } as const,
          ...(context === 'gantt'
            ? [
                {
                  name: 'labels.showOnMap',
                  value: () =>
                    CommonHelper.showOnMap(
                      this.router,
                      item.uuid,
                      this.injector,
                    ),
                  leftIcon: { name: 'map-view-outlined', size: 20 },
                } as const,
              ]
            : []),
          ...(context === 'map'
            ? [
                {
                  name: 'labels.showOnGantt',
                  value: () =>
                    CommonHelper.showOnGantt(this.router, 'vehicle', item.uuid),
                  leftIcon: { name: 'gantt-outlined', size: 20 },
                } as const,
              ]
            : []),
          {
            name: 'labels.copyLocation',
            value: () => {
              if (!item.telemetry) return;

              const coordinates = AddressHelper.composeCoordinatesLabel(
                item.telemetry,
              );
              ActionHelper.copy(coordinates, this.toastService);
            },
            leftIcon: { name: 'location-outlined', size: 20 },
            tooltip: !item.telemetry ? 'labels.noTelematicsData' : undefined,
            disabled: !item.telemetry,
          } as const,
          ...(context === 'map'
            ? [
                {
                  name: 'labels.centerVehicle',
                  value: () => injector.get(MapStore).fitVehicle(item.uuid),
                  leftIcon: { name: 'center-outlined', size: 20 },
                  keys: ['c'] satisfies string[],
                  tooltip: !item.telemetry
                    ? 'labels.noTelematicsData'
                    : undefined,
                  disabled: !item.telemetry,
                } as const,
              ]
            : []),
        ],
      },
      {
        id: '2.crew',
        options: this.fleetHelper.getCrewOptions({
          telematicsItem: item,
          delegation: TelematicsHelper.getDelegation(item),
        }),
      },
      ...(FleetTypeHelper.isVehicle(item) && this.delegationHelper.hasModule()
        ? [
            {
              id: '3.delegation',
              options: [
                ...(!delegations.length
                  ? [
                      {
                        name: 'labels.addDelegation',
                        value: () =>
                          DelegationHelper.add(
                            { telematicsItem: item },
                            this.router,
                          ),
                        leftIcon: { name: 'delegation-outlined', size: 20 },
                      } as const,
                    ]
                  : []),
                ...chain(delegations)
                  .flatMap(({ delegation, drivers }) => [
                    delegation == null
                      ? ({
                          order: 0,
                          name: 'labels.addDelegation',
                          value: () =>
                            DelegationHelper.add(
                              {
                                telematicsItem: item,
                                telematicsDriver:
                                  drivers.length !== item.drivers.length
                                    ? drivers[0]
                                    : undefined,
                              },
                              this.router,
                            ),
                          leftIcon: { name: 'delegation-outlined', size: 20 },
                          description:
                            drivers.length !== item.drivers.length
                              ? this.namePipe.transform(drivers[0].driverData)
                              : undefined,
                        } as const)
                      : null,

                    delegation?.end &&
                    (!delegation.end.address || !delegation.end.date)
                      ? ({
                          order: 1,
                          name: 'labels.setEndDateAndAddress',
                          value: () =>
                            ActionHelper.openRoute(
                              DelegationsRoute.EDIT_DELEGATION.replace(
                                ':uuid',
                                delegation.uuid,
                              ),
                            ),
                          leftIcon: { name: 'delegation-outlined', size: 20 },
                          description:
                            drivers.length !== item.drivers.length
                              ? this.namePipe.transform(drivers[0].driverData)
                              : undefined,
                        } as const)
                      : null,

                    delegation?.status.value === DelegationStatus.ACTIVE
                      ? ({
                          order: 1,
                          name: 'labels.finishDelegation',
                          value: () =>
                            this.delegationsService
                              .getDelegation(delegation.uuid)
                              .subscribe((delegation) =>
                                this.delegationHelper.openActionModal(
                                  DelegationActionModalMode.FINISH,
                                  delegation,
                                ),
                              ),
                          leftIcon: { name: 'delegation-outlined', size: 20 },
                          description: compact([
                            drivers.length !== item.drivers.length
                              ? this.namePipe.transform(drivers[0].driverData)
                              : null,
                            delegation.identifier,
                          ]).join(' · '),
                          ...(!!delegation.relatedFleetSwap &&
                            DropdownHelper.getErrorFields(
                              'labels.disabledUntilSwapCompleted',
                            )),
                        } as const)
                      : null,
                  ])
                  .filter((option) => option != null)
                  .orderBy('order')
                  .value(),
              ],
            },
          ]
        : []),
    ].filter(({ options }) => !!options.length);
  }

  public getSortedData(
    data: TelematicsItem[],
    sort: TelematicsSort,
  ): TelematicsItem[] {
    type Order = 'asc' | 'desc';

    const byOrderDelay = (order: Order) => {
      const sortingData = data.map((item) => {
        const { deadlineTime, point, bufferTime } = item.currentPoint ?? {};

        return {
          item,
          hasDeadline: !!(point && deadlineTime),
          bufferTime: bufferTime ?? Infinity,
          freeSince: item.status.freeSince ?? Infinity,
        };
      });

      return orderBy(
        sortingData,
        [
          ({ hasDeadline }) => (hasDeadline ? 0 : 1),
          ({ bufferTime }) => bufferTime,
          ({ freeSince }) => freeSince,
        ],
        ['asc', order, order === 'asc' ? 'desc' : 'asc'],
      ).map(({ item }) => item);
    };

    const byFreeSince = (order: Order) =>
      orderBy(data, ({ status }) => status.freeSince, order);

    const byRemainingDailyDrivingTime = (order: Order) =>
      orderBy(
        data,
        ({ drivers }) =>
          drivers.at(0)?.tachograph?.dailyDriving.effectiveRemaining,
        order,
      );

    const byDelegationEndTime = (order: Order) =>
      orderBy(data, ({ activeDelegations }) => min(activeDelegations), order);

    const byRegistrationNumber = (order: Order) =>
      orderBy(
        data,
        (item) => item.generalInformation.registrationNumber,
        order,
      );

    const sortedVehicles = (() => {
      switch (sort) {
        case TelematicsSort.ORDER_DELAY_DESC:
          return byOrderDelay('desc');
        case TelematicsSort.ORDER_DELAY_ASC:
          return byOrderDelay('asc');
        case TelematicsSort.FREE_SINCE_ASC:
          return byFreeSince('asc');
        case TelematicsSort.FREE_SINCE_DESC:
          return byFreeSince('desc');
        case TelematicsSort.REMAINING_DAILY_DRIVING_TIME_DESC:
          return byRemainingDailyDrivingTime('desc');
        case TelematicsSort.REMAINING_DAILY_DRIVING_TIME_ASC:
          return byRemainingDailyDrivingTime('asc');
        case TelematicsSort.DELEGATION_END_TIME_ASC:
          return byDelegationEndTime('asc');
        case TelematicsSort.DELEGATION_END_TIME_DESC:
          return byDelegationEndTime('desc');
        case TelematicsSort.REGISTRATION_NUMBER_ASC:
          return byRegistrationNumber('asc');
        case TelematicsSort.REGISTRATION_NUMBER_DESC:
          return byRegistrationNumber('desc');
      }
    })();

    return orderBy(
      sortedVehicles,
      (vehicle) => FleetTypeHelper.isVehicle(vehicle),
      'desc',
    );
  }

  public getFilteredData(
    data: TelematicsItem[],
    filters: TelematicsFilters,
    query: string,
    isSystemAdmin: boolean,
    keepUuid?: string,
  ): TelematicsItem[] {
    const filtersChecks: ((item: TelematicsItem) => boolean)[] = [];

    const vehicleStatusFilters = uniq([
      ...filters.vehicleStatus,
      ...filters.vehicleCompliance,
      ...filters.vehicleTrailerStatus,
    ]);

    if (vehicleStatusFilters.length) {
      filtersChecks.push((item) =>
        vehicleStatusFilters.some((status) => {
          switch (status) {
            case TelematicsStatusFilter.FREE_UNDER_24H:
              return [
                OnyxMapVehicleStatus.FREE_UNDER_24H,
                OnyxMapVehicleStatus.FREE_DELEGATION_UNDER_24H,
              ].includes(item.status.value);

            case TelematicsStatusFilter.FREE_OVER_24H:
              return [
                OnyxMapVehicleStatus.FREE_OVER_24H,
                OnyxMapVehicleStatus.FREE_DELEGATION_OVER_24H,
              ].includes(item.status.value);

            case TelematicsStatusFilter.FAILURE:
              return [
                OnyxMapVehicleStatus.FAILURE,
                OnyxMapVehicleStatus.FAILURE_FREE,
              ].includes(item.status.value);

            case TelematicsStatusFilter.WAITING:
              return (
                item.currentPoint &&
                RouteHelper.getStatus(item.currentPoint) ===
                  OrderPointStatus.WAITING
              );

            case TelematicsStatusFilter.LOADING:
            case TelematicsStatusFilter.UNLOADING: {
              const currentPoint = item.currentPoint;
              if (!currentPoint || currentPoint.technicalPoint) {
                return false;
              }

              const category =
                status === TelematicsStatusFilter.LOADING
                  ? OrderPointCategory.LOADING
                  : OrderPointCategory.UNLOADING;

              return (
                currentPoint.point.category === category &&
                currentPoint.point.status === OrderPointStatus.IN_PROGRESS
              );
            }

            case TelematicsStatusFilter.RIDE:
              return (
                item.currentPoint &&
                RouteHelper.getStatus(item.currentPoint) ===
                  OrderPointStatus.TO_DO
              );

            case TelematicsStatusFilter.OFF_ROUTE:
              return item.status.offRouteData?.isCritical;

            case OnyxScheduleCompliance.ON_TIME:
              return item.status.value.endsWith('-on-time');
            case OnyxScheduleCompliance.DELAY_RISK:
              return item.status.value.endsWith('-delay-risk');
            case OnyxScheduleCompliance.DELAYED:
              return item.status.value.endsWith('-delayed');

            case TelematicsTrailerFilter.TRAILER_FREE: {
              return [
                OnyxMapVehicleStatus.TRAILER_FREE,
                OnyxMapVehicleStatus.SEMI_TRAILER_FREE,
              ].includes(item.status.value);
            }
            case TelematicsTrailerFilter.TRAILER_LOADED: {
              return [
                OnyxMapVehicleStatus.TRAILER_LOADED,
                OnyxMapVehicleStatus.SEMI_TRAILER_LOADED,
              ].includes(item.status.value);
            }
            case TelematicsTrailerFilter.TRAILER_LOADED_WAITING:
              return [
                OnyxMapVehicleStatus.TRAILER_LOADED_WAITING,
                OnyxMapVehicleStatus.SEMI_TRAILER_LOADED_WAITING,
              ].includes(item.status.value);
          }
        }),
      );
    }

    if (filters.vehicleTachographStatus.length) {
      filtersChecks.push(({ drivers }) =>
        filters.vehicleTachographStatus.some(
          (status) => drivers.at(0)?.tachograph?.workingMode === status,
        ),
      );
    }

    {
      const isAddressInRadius = (
        coordinates: OnyxCoordinates,
        address: OnyxCustomAddress,
        radius: number,
      ): boolean => {
        const distance = AddressHelper.getHaversineDistance(
          coordinates,
          address,
        );
        return distance <= radius;
      };

      const { currentAddress, currentRadius, finalAddress, finalRadius } =
        filters ?? {};

      if (currentAddress && currentRadius != null) {
        filtersChecks.push(
          ({ telemetry }) =>
            telemetry != null &&
            isAddressInRadius(
              telemetry.coordinates,
              currentAddress,
              currentRadius,
            ),
        );
      }

      if (finalAddress && finalRadius != null) {
        filtersChecks.push((item) => {
          const finalCoordinates = (() => {
            const lastPoint = item.routePoints.at(-1)?.point;
            if (!lastPoint) return item.telemetry?.coordinates ?? null;

            const address =
              'address' in lastPoint ? lastPoint.address : lastPoint.endAddress;
            return address.coordinates;
          })();
          if (!finalCoordinates) return false;

          return isAddressInRadius(finalCoordinates, finalAddress, finalRadius);
        });
      }
    }

    if (filters.employees.length) {
      filtersChecks.push((item) =>
        filters.employees.some((employee) =>
          item.assignedEmployees.some(
            (assignedEmployee) => assignedEmployee.uuid === employee,
          ),
        ),
      );
    }

    if (filters.onlyVehiclesWithProposals) {
      filtersChecks.push((item) =>
        item.orders.some(
          ({ order }) => order?.status.value === OrderStatus.TO_ACCEPT,
        ),
      );
    }

    if (filters.planningMode) {
      filtersChecks.push(
        (item) => item.generalInformation.planningMode === filters.planningMode,
      );
    }

    if (filters.countries.length) {
      filtersChecks.push(({ allowedCountries }) =>
        filters.countries.some((country) =>
          allowedCountries.whitelist.includes(country),
        ),
      );
    }

    if (filters.route.length) {
      filtersChecks.push(({ routePoints }) =>
        filters.route.every((filter) =>
          routePoints.some((routePoint) => {
            switch (filter) {
              case TelematicsRouteFilter.PLANNED:
                return routePoint.order?.route.isVerified;
              case TelematicsRouteFilter.UNPLANNED:
                return !routePoint.order?.route.isVerified;
            }
          }),
        ),
      );
    }

    {
      const availableTimeFilters = {
        remainingDailyDrivingTime: 'dailyDriving',
        remainingWeeklyDrivingTime: 'weeklyDriving',
        remainingFortnightDrivingTime: 'fortnightDriving',
      } as const;

      for (const [filterKey, tachographKey] of Object.entries(
        availableTimeFilters,
      )) {
        const remainingTime = filters[filterKey as keyof TelematicsFilters];
        if (remainingTime == null) continue;

        filtersChecks.push(
          ({ drivers }) =>
            !!drivers?.length &&
            drivers.some(
              ({ tachograph }) =>
                tachograph?.[tachographKey] != null &&
                tachograph[tachographKey].effectiveRemaining <=
                  (remainingTime as number) *
                    Duration.fromObject({ hour: 1 }).as('seconds'),
            ),
        );
      }
    }

    filtersChecks.push(({ activeDelegations }) => {
      switch (filters.delegation) {
        case TelematicsDelegationFilter.ACTIVE:
          return activeDelegations.length !== 0;

        case TelematicsDelegationFilter.NONE:
          return activeDelegations.length === 0;

        case TelematicsDelegationFilter.ALL:
          return true;
      }
    });

    const { delegationEnd } = filters;
    if (delegationEnd) {
      const filterDate = DateTime.fromISO(delegationEnd);

      filtersChecks.push((item) => {
        const nearestDelegation = minBy(
          item.activeDelegations,
          (delegation) => delegation.plannedEnd,
        );
        if (!nearestDelegation?.plannedEnd) return false;

        return nearestDelegation.plannedEnd.hasSame(filterDate, 'day');
      });
    }

    filtersChecks.push(({ drivers }) => {
      switch (filters.crew) {
        case TelematicsCrewFilter.SINGLE:
          return drivers.length === 1;

        case TelematicsCrewFilter.DOUBLE:
          return drivers.length === 2;

        case TelematicsCrewFilter.ALL:
          return true;
      }
    });

    if (filters.fuelType.length) {
      filtersChecks.push(({ fuelTankCapacity }) =>
        fuelTankCapacity?.fuelType
          ? filters.fuelType.includes(
              fuelTankCapacity.fuelType === FuelType.LNG_DIESEL
                ? FuelType.DIESEL_LNG
                : fuelTankCapacity.fuelType,
            )
          : false,
      );
    }

    if (filters.remainingFuel) {
      filtersChecks.push(({ telemetry, fuelTankCapacity }) => {
        if (!telemetry) return false;

        const { fuelLevel } = telemetry;
        const { totalCapacity } = fuelTankCapacity ?? {};
        if (fuelLevel == null || !totalCapacity) return false;

        const fuelPercentage = Math.ceil((fuelLevel / totalCapacity) * 100);
        switch (filters.remainingFuel) {
          case TelematicsRemainingFuelFilter.UNDER_10_PERCENTAGE:
            return fuelPercentage < 10;

          case TelematicsRemainingFuelFilter.UNDER_25_PERCENTAGE:
            return fuelPercentage < 25;

          default:
            return false;
        }
      });
    }

    filtersChecks.push(({ orders }) => {
      switch (filters.cargo) {
        case TelematicsCargoFilter.LTL:
          return orders.some(({ order }) => !order?.isDedicated);

        case TelematicsCargoFilter.FTL:
          return orders.some(({ order }) => order?.isDedicated);

        case TelematicsCargoFilter.ALL:
          return true;
      }
    });

    {
      const {
        vehicleCategory,
        adrClasses,
        cargoSpaceType,
        onlyLowDeck,
        semiTrailerSize,
        onlyInactive,
      } = filters ?? {};

      if (vehicleCategory?.length) {
        filtersChecks.push((item) =>
          vehicleCategory.some(
            (category) => item.generalInformation.category === category,
          ),
        );
      }

      if (semiTrailerSize?.length) {
        filtersChecks.push((item) =>
          semiTrailerSize.some(
            (size) => item.parameters.semiTrailerSize === size,
          ),
        );
      }

      if (adrClasses?.length) {
        filtersChecks.push((item) =>
          adrClasses.some((adrClass) =>
            item.parameters.adrClasses?.includes(adrClass),
          ),
        );
      }

      if (cargoSpaceType?.length) {
        filtersChecks.push((item) =>
          cargoSpaceType.some((type) => item.generalInformation.type === type),
        );
      }

      if (onlyLowDeck) {
        filtersChecks.push((item) => item.parameters.isLowDeck === onlyLowDeck);
      }

      if (onlyInactive) {
        filtersChecks.push((item) => !item.telemetry);
      }
    }

    return chain(data)
      .filter(
        (item) =>
          item.uuid === keepUuid || filtersChecks.every((check) => check(item)),
      )
      .thru((vehicles) => {
        if (!query) return vehicles;

        return this.filterPipe.transform(vehicles, query, [
          (item) => this.telematicsIdentifierPipe.transform(item, 'long'),
          ({ drivers }) =>
            drivers
              .map(({ driverData }) => this.namePipe.transform(driverData))
              .join(', '),
          ({ orders }) =>
            orders
              .flatMap(({ order }) => [
                order?.identifier,
                order?.sender?.companyProfile?.displayName,
              ])
              .join(', '),

          ...(isSystemAdmin
            ? [
                (item: TelematicsItem) => item.uuid,
                (item: TelematicsItem) =>
                  item.orders.flatMap(({ order }) => order?.uuid).join(', '),
              ]
            : []),
        ]);
      })
      .value();
  }
}
